import { Box, Stack, Typography } from '@mui/material'
import CloudIcon from '@mui/icons-material/Cloud'
import DevicesIcon from '@mui/icons-material/Devices'
import HttpsIcon from '@mui/icons-material/Https'
import LockIcon from '@mui/icons-material/Lock'
import MapIcon from '@mui/icons-material/Map'
import ShieldIcon from '@mui/icons-material/Shield'
import StorageIcon from '@mui/icons-material/Storage'
import { useScrollReveal } from '../use-scroll-reveal'

const PROTECTION_POINTS = [
  {
    icon: <LockIcon />,
    title: 'Your password is never stored by us',
    body: 'Login is handled by Supabase Auth, a secure third-party authentication service. Your password is hashed server-side before it ever touches a database — we never see it, and neither does anyone else.',
  },
  {
    icon: <HttpsIcon />,
    title: 'All data travels encrypted',
    body: 'Every request between your browser and our servers uses HTTPS — the same encryption standard used by banks. Your data is never sent in plain text.',
  },
  {
    icon: <MapIcon />,
    title: 'Your location stays yours',
    body: "Your farm's coordinates are used only for displaying your map, estimating your local climate, and generating farm overview images. They are never sold, shared with advertisers, or used for anything outside the app.",
  },
  {
    icon: <StorageIcon />,
    title: 'Temporary in-memory caching',
    body: 'To keep things fast, the app stores fetched data in browser memory — this clears automatically when you close the tab. Nothing sensitive is saved to your device storage except your session token, which is managed securely by Supabase.',
  },
  {
    icon: <DevicesIcon />,
    title: 'Demo access & device fingerprinting',
    body: "If you access the app via a demo QR code, your browser generates a lightweight device fingerprint (screen size, language, timezone, hardware threads) to track your 7-day access window. This fingerprint is stored on your device and linked to the ticket in our database — it is not tied to your personal identity and is only used to manage demo access.",
  },
  {
    icon: <CloudIcon />,
    title: 'Hosted on trusted infrastructure',
    body: 'The app is served from Cloudflare, a secure HTTPS-only global network, and all data resides on Supabase Cloud infrastructure — both are trusted, security-focused platforms used by thousands of applications worldwide.',
  },
]

const ProtectionPoint = ({ point, index }) => {
  const { ref, visible } = useScrollReveal(0.1)
  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        gap: 2,
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.5s ease ${index * 0.08}s, transform 0.5s ease ${index * 0.08}s`,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: 'primary.main',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 0.25,
          opacity: 0.85,
        }}
      >
        {point.icon}
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {point.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
          {point.body}
        </Typography>
      </Box>
    </Box>
  )
}

export const DataProtectionSection = () => {
  const { ref, visible } = useScrollReveal(0.1)

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 3, md: 6 }, maxWidth: 900, mx: 'auto' }}>
      <Box
        ref={ref}
        sx={{
          textAlign: 'center',
          mb: 6,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}
      >
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} mb={1}>
          <ShieldIcon color="primary" />
          <Typography variant="overline" color="primary" fontWeight={600} letterSpacing={2}>
            Data Protection
          </Typography>
        </Stack>
        <Typography variant="h4" fontWeight={700}>
          We take your data seriously
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1.5} sx={{ maxWidth: 520, mx: 'auto' }}>
          Plain language — no legal jargon. Here's exactly how your information is handled.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
        {PROTECTION_POINTS.map((point, i) => (
          <ProtectionPoint key={point.title} point={point} index={i} />
        ))}
      </Box>
    </Box>
  )
}
